'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createMindcareBooking,
  createMindcareContentRequest,
  createMindcareSessionRoom,
  createMindcareJournalEntry,
  createMindcareTherapistWithdrawal,
  deleteMindcarePatientAccount,
  confirmMindcareBookingPayment,
  endMindcareSessionRoom,
  enrollMindcareTherapist,
  fetchMindcareAdminTherapists,
  fetchMindcareAdminWithdrawals,
  fetchMindcareBarazas,
  fetchMindcareBootstrap,
  fetchMindcareContentRequests,
  fetchMindcareDashboard,
  fetchMindcarePatientForms,
  fetchMindcarePatientPortal,
  fetchMindcareResources,
  fetchMindcareJournalPrompts,
  fetchMindcareSessionRoom,
  fetchMindcareTherapist,
  fetchMindcareTherapistAccount,
  fetchMindcareTherapistEarnings,
  fetchMindcareTherapistForms,
  fetchMindcareTherapists,
  loginMindcarePatient,
  loginMindcareTherapist,
  requestMindcarePasswordReset,
  requestMindcarePatientDataDeletion,
  reviewMindcarePatientForm,
  initiateMindcareBookingPayment,
  updateMindcareContentRequest,
  updateMindcareTherapistApproval,
  updateMindcareAdminWithdrawal,
  updateMindcareTherapistProfile,
  upsertMindcareBaraza,
  upsertMindcareJournalPrompt,
  upsertMindcareResource,
  upsertMindcarePatient,
  upsertMindcarePatientForm,
  updateMindcareAvailability,
  verifyMindcarePasswordReset,
} from '../api/mindcareApi';
import type {
  CreateMindcareBookingPayload,
  UpdateMindcareAvailabilityPayload,
} from '@mindcare/shared/types';

export const mindcareKeys = {
  bootstrap: ['mindcare', 'bootstrap'] as const,
  therapists: ['mindcare', 'therapists'] as const,
  therapist: (slug: string) => ['mindcare', 'therapist', slug] as const,
  therapistAccount: ['mindcare', 'therapist-account'] as const,
  dashboard: (email?: string) => ['mindcare', 'dashboard', email || 'guest'] as const,
  patientPortal: (email?: string) => ['mindcare', 'patient-portal', email || 'guest'] as const,
  patientForms: (email?: string) => ['mindcare', 'patient-forms', email || 'guest'] as const,
  adminTherapists: ['mindcare', 'admin', 'therapists'] as const,
  barazas: ['mindcare', 'barazas'] as const,
  resources: ['mindcare', 'resources'] as const,
  journalPrompts: ['mindcare', 'journal-prompts'] as const,
  contentRequests: ['mindcare', 'admin', 'content-requests'] as const,
  sessionRoom: (roomId: string) => ['mindcare', 'session-room', roomId] as const,
  therapistEarnings: (slug: string) => ['mindcare', 'therapist', slug, 'earnings'] as const,
  therapistForms: (slug: string) => ['mindcare', 'therapist', slug, 'forms'] as const,
};

export function useMindcareBootstrap(backendUrl: string) {
  return useQuery({
    queryKey: mindcareKeys.bootstrap,
    queryFn: () => fetchMindcareBootstrap(backendUrl),
  });
}

export function useMindcareTherapists(backendUrl: string) {
  return useQuery({
    queryKey: mindcareKeys.therapists,
    queryFn: () => fetchMindcareTherapists(backendUrl),
  });
}

export function useMindcareTherapist(backendUrl: string, slug: string) {
  return useQuery({
    queryKey: mindcareKeys.therapist(slug),
    queryFn: () => fetchMindcareTherapist(backendUrl, slug),
    enabled: Boolean(slug),
  });
}

export function useMindcareTherapistAccount(backendUrl: string, authToken: string) {
  return useQuery({
    queryKey: [...mindcareKeys.therapistAccount, authToken ? authToken.slice(-12) : 'guest'],
    queryFn: () => fetchMindcareTherapistAccount(backendUrl, authToken),
    enabled: Boolean(authToken),
    retry: false,
  });
}

export function useMindcareTherapistEarnings(
  backendUrl: string,
  slug: string,
  auth: string | { email: string; password: string } = '',
) {
  const authToken = typeof auth === 'string' ? auth : '';
  return useQuery({
    queryKey: [...mindcareKeys.therapistEarnings(slug), authToken ? authToken.slice(-12) : 'guest'],
    queryFn: () => fetchMindcareTherapistEarnings(backendUrl, slug, authToken),
    enabled: Boolean(slug && authToken),
  });
}

export function useMindcareDashboard(backendUrl: string, email?: string) {
  return useQuery({
    queryKey: mindcareKeys.dashboard(email),
    queryFn: () => fetchMindcareDashboard(backendUrl, email),
  });
}

export function useMindcarePatientPortal(backendUrl: string, email?: string) {
  return useQuery({
    queryKey: mindcareKeys.patientPortal(email),
    queryFn: () => fetchMindcarePatientPortal(backendUrl, email),
  });
}

export function useMindcarePatientForms(backendUrl: string, email?: string) {
  return useQuery({
    queryKey: mindcareKeys.patientForms(email),
    queryFn: () => fetchMindcarePatientForms(backendUrl, email),
    enabled: Boolean(email),
  });
}

export function useMindcareBarazas(backendUrl: string) {
  return useQuery({
    queryKey: mindcareKeys.barazas,
    queryFn: () => fetchMindcareBarazas(backendUrl),
  });
}

export function useMindcareResources(backendUrl: string) {
  return useQuery({
    queryKey: mindcareKeys.resources,
    queryFn: () => fetchMindcareResources(backendUrl),
  });
}

export function useMindcareJournalPrompts(backendUrl: string) {
  return useQuery({
    queryKey: mindcareKeys.journalPrompts,
    queryFn: () => fetchMindcareJournalPrompts(backendUrl),
  });
}

export function useMindcareContentRequests(backendUrl: string) {
  return useQuery({
    queryKey: mindcareKeys.contentRequests,
    queryFn: () => fetchMindcareContentRequests(backendUrl),
  });
}

export function useCreateMindcareBooking(backendUrl: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMindcareBookingPayload) => createMindcareBooking(backendUrl, payload),
    onSuccess: (_booking, payload) => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.therapists });
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.therapist(payload.therapistSlug) });
      void queryClient.invalidateQueries({ queryKey: ['mindcare', 'dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['mindcare', 'patient-portal'] });
    },
  });
}

export function useInitiateMindcareBookingPayment(backendUrl: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookingId,
      ...payload
    }: {
      bookingId: string;
      provider: 'mpesa' | 'paystack';
      phone?: string;
      callbackUrl?: string;
    }) => initiateMindcareBookingPayment(backendUrl, bookingId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mindcare', 'patient-portal'] });
      void queryClient.invalidateQueries({ queryKey: ['mindcare', 'dashboard'] });
    },
  });
}

export function useConfirmMindcareBookingPayment(backendUrl: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookingId,
      ...payload
    }: {
      bookingId: string;
      provider?: 'mpesa' | 'paystack';
      reference?: string;
    }) => confirmMindcareBookingPayment(backendUrl, bookingId, payload),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['mindcare', 'patient-portal'] });
      void queryClient.invalidateQueries({ queryKey: ['mindcare', 'dashboard'] });
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.sessionRoom(result.room.id) });
    },
  });
}

export function useUpsertMindcarePatient(backendUrl: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      name: string;
      email: string;
      phone?: string;
      country?: string;
      emergencyContact?: string;
      clientCategory?: 'student' | 'adult';
      consentAccepted?: boolean;
      password?: string;
    }) => upsertMindcarePatient(backendUrl, payload),
    onSuccess: (patient) => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.patientPortal(patient.email) });
    },
  });
}

export function useRequestMindcarePatientDataDeletion(backendUrl: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof requestMindcarePatientDataDeletion>[1]) =>
      requestMindcarePatientDataDeletion(backendUrl, payload),
    onSuccess: (_result, payload) => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.patientPortal(payload.email) });
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.patientForms(payload.email) });
    },
  });
}

export function useDeleteMindcarePatientAccount(backendUrl: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof deleteMindcarePatientAccount>[1]) =>
      deleteMindcarePatientAccount(backendUrl, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mindcare'] });
    },
  });
}

export function useUpsertMindcarePatientForm(backendUrl: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof upsertMindcarePatientForm>[1]) => upsertMindcarePatientForm(backendUrl, payload),
    onSuccess: (form) => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.patientForms(form.clientEmail) });
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.patientPortal(form.clientEmail) });
    },
  });
}

export function useMindcareSessionRoom(backendUrl: string, roomId: string) {
  return useQuery({
    queryKey: mindcareKeys.sessionRoom(roomId),
    queryFn: () => fetchMindcareSessionRoom(backendUrl, roomId),
  });
}

export function useCreateMindcareSessionRoom(backendUrl: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bookingId: string) => createMindcareSessionRoom(backendUrl, bookingId),
    onSuccess: (room) => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.sessionRoom(room.id) });
      void queryClient.invalidateQueries({ queryKey: ['mindcare', 'dashboard'] });
    },
  });
}

export function useEndMindcareSessionRoom(backendUrl: string, roomId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (summary?: string) => endMindcareSessionRoom(backendUrl, roomId, summary),
    onSuccess: (room) => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.sessionRoom(room.id) });
      void queryClient.invalidateQueries({ queryKey: ['mindcare', 'dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['mindcare', 'patient-portal'] });
    },
  });
}

export function useUpdateMindcareAvailability(backendUrl: string, slug: string, authToken = '') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateMindcareAvailabilityPayload) => updateMindcareAvailability(backendUrl, slug, payload, authToken),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.therapists });
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.therapist(slug) });
    },
  });
}

export function useCreateMindcareTherapistWithdrawal(backendUrl: string, slug: string, authToken = '') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof createMindcareTherapistWithdrawal>[3]) =>
      createMindcareTherapistWithdrawal(backendUrl, slug, authToken, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.therapistEarnings(slug) });
    },
  });
}

export function useMindcareTherapistForms(
  backendUrl: string,
  slug: string,
  authToken = '',
) {
  return useQuery({
    queryKey: [...mindcareKeys.therapistForms(slug), authToken ? authToken.slice(-12) : 'guest'],
    queryFn: () => fetchMindcareTherapistForms(backendUrl, slug, authToken),
    enabled: Boolean(slug && authToken),
  });
}

export function useReviewMindcarePatientForm(backendUrl: string, slug: string, authToken = '') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ formId, ...payload }: { formId: string; therapistNotes?: string }) =>
      reviewMindcarePatientForm(backendUrl, slug, formId, authToken, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.therapistForms(slug) });
    },
  });
}

export function useEnrollMindcareTherapist(backendUrl: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof enrollMindcareTherapist>[1]) => enrollMindcareTherapist(backendUrl, payload),
    onSuccess: (therapist) => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.adminTherapists });
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.therapists });
      if (therapist?.slug) {
        void queryClient.invalidateQueries({ queryKey: mindcareKeys.therapist(therapist.slug) });
      }
    },
  });
}

export function useLoginMindcareTherapist(backendUrl: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { email: string; password: string }) => loginMindcareTherapist(backendUrl, payload),
    onSuccess: (therapist) => {
      if (therapist?.slug) {
        void queryClient.invalidateQueries({ queryKey: mindcareKeys.therapist(therapist.slug) });
      }
    },
  });
}

export function useLoginMindcarePatient(backendUrl: string) {
  return useMutation({
    mutationFn: (payload: { email: string; password: string }) => loginMindcarePatient(backendUrl, payload),
  });
}

export function useRequestMindcarePasswordReset(backendUrl: string) {
  return useMutation({
    mutationFn: (payload: { role: 'patient' | 'therapist'; email: string }) => requestMindcarePasswordReset(backendUrl, payload),
  });
}

export function useVerifyMindcarePasswordReset(backendUrl: string) {
  return useMutation({
    mutationFn: (payload: { role: 'patient' | 'therapist'; email: string; otp: string; password: string }) =>
      verifyMindcarePasswordReset(backendUrl, payload),
  });
}

export function useMindcareAdminTherapists(backendUrl: string) {
  return useQuery({
    queryKey: mindcareKeys.adminTherapists,
    queryFn: () => fetchMindcareAdminTherapists(backendUrl),
  });
}

export function useUpdateMindcareTherapistProfile(backendUrl: string, slug: string, authToken: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof updateMindcareTherapistProfile>[3]) =>
      updateMindcareTherapistProfile(backendUrl, slug, authToken, payload),
    onSuccess: (therapist) => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.therapistAccount });
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.therapist(slug) });
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.therapists });
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.adminTherapists });
    },
  });
}

export function useMindcareAdminWithdrawals(backendUrl: string, adminToken: string) {
  return useQuery({
    queryKey: ['mindcare', 'admin', 'withdrawals'],
    queryFn: () => fetchMindcareAdminWithdrawals(backendUrl, adminToken),
    enabled: Boolean(adminToken),
  });
}

export function useUpdateMindcareAdminWithdrawal(backendUrl: string, adminToken: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string; status: 'approved' | 'rejected' | 'paid'; payoutReference?: string; adminNotes?: string }) =>
      updateMindcareAdminWithdrawal(backendUrl, adminToken, id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mindcare', 'admin', 'withdrawals'] });
    },
  });
}

export function useUpdateMindcareTherapistApproval(backendUrl: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, ...payload }: { slug: string; approvalStatus: string; adminNotes?: string }) =>
      updateMindcareTherapistApproval(backendUrl, slug, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.adminTherapists });
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.therapists });
    },
  });
}

export function useUpsertMindcareBaraza(backendUrl: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof upsertMindcareBaraza>[1]) => upsertMindcareBaraza(backendUrl, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.barazas });
    },
  });
}

export function useUpsertMindcareResource(backendUrl: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof upsertMindcareResource>[1]) => upsertMindcareResource(backendUrl, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.resources });
    },
  });
}

export function useUpsertMindcareJournalPrompt(backendUrl: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof upsertMindcareJournalPrompt>[1]) => upsertMindcareJournalPrompt(backendUrl, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.journalPrompts });
    },
  });
}

export function useCreateMindcareContentRequest(backendUrl: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof createMindcareContentRequest>[1]) => createMindcareContentRequest(backendUrl, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.contentRequests });
    },
  });
}

export function useUpdateMindcareContentRequest(backendUrl: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string; status?: string; adminNotes?: string }) =>
      updateMindcareContentRequest(backendUrl, id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mindcareKeys.contentRequests });
    },
  });
}

export function useCreateMindcareJournalEntry(backendUrl: string) {
  return useMutation({
    mutationFn: (payload: { clientEmail?: string; title: string; mood: string; body: string; tags?: string[] }) =>
      createMindcareJournalEntry(backendUrl, payload),
  });
}
